/**
 * Day planning (PRD M6) and its rules (PRD M7).
 *
 * A day is planned in numbered vehicles of the day: NV° 1, 2, 3... (shown V1, V2...). Each one gets a
 * physical van (plate), a guide and a set of bookings, possibly of several tours (a T1 van can take
 * half-day passengers, a T2M morning can be followed by a T3A afternoon).
 *
 * This module is pure: the browser uses it to answer a drag at once, the server uses the same code to
 * refuse an invalid save, so the two never disagree.
 */
import { en } from './i18n.js';
export const SEGMENT_LABELS = { am: 'Morning', pm: 'Afternoon', eve: 'Evening' };
export const DEFAULT_SEATS = 8;
/** Who drives one trip of a transfer vehicle. */
export function driverOf(dv, time, tripDrivers) {
    return tripDrivers?.find((x) => x.dayVehicleId === dv.id && x.time === (time ?? ''))?.guideId ?? dv.guideId;
}
/** Minutes between two transfer trips of the same vehicle below which the office is warned. */
export const TRIP_GAP_MINUTES = 60;
/** Transfers of a vehicle grouped in trips: same time = same trip. Sorted by time. */
export function tripsOf(bookings) {
    const by = new Map();
    for (const b of bookings) {
        const k = b.transferTime ?? '';
        by.set(k, [...(by.get(k) ?? []), b]);
    }
    return [...by.entries()].sort(([a], [b]) => (a || '99').localeCompare(b || '99')).map(([time, list]) => ({ time: time || null, bookings: list }));
}
const minutes = (hhmm) => Number(hhmm.slice(0, 2)) * 60 + Number(hhmm.slice(3, 5));
export const DEPARTURES = ['guaranteed', 'cancelled'];
const normLang = (l) => {
    const v = (l ?? '').trim().toUpperCase();
    return v === 'SP' ? 'ES' : v;
};
const live = (b) => b.status !== 'X';
/** Which parts of the day a booking keeps its vehicle busy. Unknown shifts follow the pickup time. */
export function segmentsOf(b, tour) {
    switch (tour?.shift) {
        case 'morning':
            return ['am'];
        case 'afternoon':
            return ['pm'];
        case 'full_day':
            return ['am', 'pm'];
        case 'evening':
            return ['eve'];
        default: {
            const t = b.pickupTime ?? '';
            if (!t || t < '12:00')
                return ['am'];
            if (t < '17:00')
                return ['pm'];
            return ['eve'];
        }
    }
}
export function seatsOf(dv, vehicles) {
    if (dv.seats)
        return dv.seats;
    return vehicles.find((v) => v.id === dv.vehicleId)?.seats ?? DEFAULT_SEATS;
}
const refMatches = (ref, t) => {
    const r = ref.trim().toUpperCase();
    if (r.startsWith('FAMILY:'))
        return !!t.family && r.slice(7) === t.family.toUpperCase();
    return r === t.code.toUpperCase();
};
/** The rule that decides whether two tours may share a vehicle; highest priority wins. */
export function pairRule(a, b, rules) {
    let best;
    for (const r of rules) {
        if (r.active === false)
            continue;
        const hit = (refMatches(r.leftRef, a) && refMatches(r.rightRef, b)) || (refMatches(r.leftRef, b) && refMatches(r.rightRef, a));
        if (hit && (!best || r.priority > best.priority))
            best = r;
    }
    return best;
}
/** Pax on board per part of the day; the highest is what must fit in the seats. */
export function loadOf(bookings, tours) {
    const load = { am: 0, pm: 0, eve: 0 };
    for (const b of bookings) {
        if (!live(b))
            continue;
        for (const s of segmentsOf(b, b.tourId ? tours.get(b.tourId) : undefined))
            load[s] += b.pax;
    }
    return load;
}
/** The guide driving a vehicle of the day in a part of the day. */
export function guideFor(dv, seg) {
    return seg === 'am' ? dv.guideId : dv.pmGuideId ?? dv.guideId;
}
const dirOf = (t, s) => (s === 'am' ? t.directionMorning : s === 'pm' ? t.directionAfternoon : null);
/** Every problem of a day plan. Blocks are refused; warnings land and show amber. */
export function checkDay(input, t = en) {
    const tours = new Map(input.tours.map((t) => [t.id, t]));
    const dayTours = new Map((input.dayTours ?? []).map((d) => [d.tourId, d]));
    const guides = new Map(input.guides.map((g) => [g.id, g]));
    const out = [];
    const maxSeats = Math.max(DEFAULT_SEATS, ...input.vehicles.map((v) => v.seats), ...input.dayVehicles.map((d) => d.seats ?? 0));
    // Tours: same guide in two vehicles at the same part of the day. Same plate twice among vehicles of the same kind:
    // a van may carry tours and transfers on the same day when the times allow (Bruno, 19/10/2026).
    const seenGuide = new Map();
    const seenVehicle = new Map();
    const sorted = [...input.dayVehicles].sort((a, b) => a.number - b.number);
    for (const dv of sorted) {
        const reported = new Set();
        for (const seg of dv.kind === 'transfers' ? [] : ['am', 'pm', 'eve']) {
            const g = guideFor(dv, seg);
            if (!g)
                continue;
            const first = seenGuide.get(`${g}|${seg}`);
            if (first !== undefined && first !== dv.number) {
                if (!reported.has(g)) {
                    const when = !!dv.pmGuideId;
                    const whenPart = t(SEGMENT_LABELS[seg]).toLowerCase();
                    out.push({ level: 'block', code: 'guide_twice', dayVehicleId: dv.id, message: t(when ? '{guide} is already in V{n} in the {part}.' : '{guide} is already in V{n}.', { guide: guides.get(g)?.name ?? t('This guide'), n: first, part: whenPart }) });
                    reported.add(g);
                }
            }
            else
                seenGuide.set(`${g}|${seg}`, dv.number);
        }
        if (dv.vehicleId) {
            const key = `${dv.kind ?? 'tours'}|${dv.vehicleId}`;
            const first = seenVehicle.get(key);
            const plate = input.vehicles.find((v) => v.id === dv.vehicleId)?.plate ?? 'This van';
            if (first !== undefined)
                out.push({ level: 'block', code: 'vehicle_twice', dayVehicleId: dv.id, message: t('{plate} is already V{n}.', { plate, n: first }) });
            else
                seenVehicle.set(key, dv.number);
        }
    }
    // Transfer trips against the tours of the same van or the same guide, and one driver on two trips at once.
    // Warnings only: the tours are known by part of the day, the office knows the real times.
    {
        const placedLive = input.bookings.filter(live);
        const tourVs = sorted.filter((dv) => dv.kind !== 'transfers');
        const busy = new Map();
        for (const dv of tourVs) {
            const segs = new Set();
            for (const b of placedLive)
                if (b.dayVehicleId === dv.id)
                    for (const s of segmentsOf(b, b.tourId ? tours.get(b.tourId) : undefined))
                        segs.add(s);
            busy.set(dv.id, segs);
        }
        const driving = [];
        for (const dv of sorted.filter((x) => x.kind === 'transfers')) {
            for (const trip of tripsOf(placedLive.filter((b) => b.dayVehicleId === dv.id && b.transfer))) {
                if (!trip.time)
                    continue;
                const seg = segmentsOf({ pickupTime: trip.time }, undefined)[0];
                const part = t(SEGMENT_LABELS[seg]).toLowerCase();
                const ids = trip.bookings.map((b) => b.id);
                const v = `V${dv.number}`;
                for (const tv of tourVs) {
                    if (!busy.get(tv.id).has(seg))
                        continue;
                    if (dv.vehicleId && tv.vehicleId === dv.vehicleId) {
                        const plate = input.vehicles.find((x) => x.id === dv.vehicleId)?.plate ?? t('This van');
                        out.push({ level: 'warning', code: 'vehicle_busy', dayVehicleId: dv.id, bookingIds: ids, message: t('{plate}: the {time} trip of {v} falls during the tours of V{n} ({part}).', { plate, time: trip.time, v, part, n: tv.number }) });
                    }
                }
                const g = driverOf(dv, trip.time, input.tripDrivers);
                if (!g)
                    continue;
                const name = guides.get(g)?.name ?? t('This guide');
                for (const tv of tourVs) {
                    if (busy.get(tv.id).has(seg) && guideFor(tv, seg) === g) {
                        out.push({ level: 'warning', code: 'driver_busy', dayVehicleId: dv.id, bookingIds: ids, message: t('{plate}: the {time} trip of {v} falls during the tours of V{n} ({part}).', { plate: name, time: trip.time, v, part, n: tv.number }) });
                    }
                }
                driving.push({ dv, time: trip.time, guide: g, ids });
            }
        }
        driving.sort((a, b) => a.time.localeCompare(b.time));
        for (let i = 0; i < driving.length; i++) {
            for (let j = i + 1; j < driving.length; j++) {
                const a = driving[i];
                const b = driving[j];
                if (minutes(b.time) - minutes(a.time) >= TRIP_GAP_MINUTES)
                    break;
                if (a.guide !== b.guide || a.dv.id === b.dv.id)
                    continue;
                out.push({ level: 'warning', code: 'driver_busy', dayVehicleId: b.dv.id, bookingIds: [...a.ids, ...b.ids], message: t('{guide} drives V{a} at {x} and V{b} at {y}.', { guide: guides.get(a.guide)?.name ?? t('This guide'), a: a.dv.number, x: a.time, b: b.dv.number, y: b.time }) });
            }
        }
    }
    for (const dv of input.dayVehicles) {
        const inside = input.bookings.filter((b) => b.dayVehicleId === dv.id);
        const placed = inside.filter(live);
        const v = `V${dv.number}`;
        for (const b of inside.filter((x) => !live(x))) {
            out.push({ level: 'block', code: 'cancelled_placed', dayVehicleId: dv.id, bookingIds: [b.id], message: t('{ref} is cancelled and cannot be in a vehicle.', { ref: b.reference }) });
        }
        if (!placed.length)
            continue;
        const offTours = placed.filter((b) => b.tourId && dayTours.get(b.tourId)?.departure === 'cancelled');
        if (offTours.length) {
            out.push({ level: 'block', code: 'departure_cancelled', dayVehicleId: dv.id, bookingIds: offTours.map((b) => b.id), message: t(offTours.length > 1 ? '{tours} does not run today: take these bookings out of {v}.' : '{tours} does not run today: take this booking out of {v}.', { tours: [...new Set(offTours.map((b) => tours.get(b.tourId)?.code))].join(', '), v }) });
        }
        if (!dv.vehicleId)
            out.push({ level: 'warning', code: 'no_vehicle', dayVehicleId: dv.id, message: t('{v} has no van yet.', { v }) });
        const driverless = dv.kind === 'transfers'
            ? tripsOf(placed.filter((x) => x.transfer)).some((trip) => !driverOf(dv, trip.time, input.tripDrivers))
            : !dv.guideId;
        if (driverless)
            out.push({ level: 'warning', code: 'no_guide', dayVehicleId: dv.id, message: t('{v} has no guide yet.', { v }) });
        // Tours and transfers never share a vehicle of the day.
        const isTransferV = dv.kind === 'transfers';
        const wrong = placed.filter((b) => !!b.transfer !== isTransferV);
        for (const b of wrong) {
            out.push({ level: 'block', code: 'wrong_kind', dayVehicleId: dv.id, bookingIds: [b.id], message: t(isTransferV ? '{ref} is a tour: {v} carries transfers only.' : '{ref} is a transfer: put it in a transfer vehicle.', { ref: b.reference, v }) });
        }
        if (isTransferV) {
            const seatsT = seatsOf(dv, input.vehicles);
            const trips = tripsOf(placed.filter((b) => b.transfer));
            for (const trip of trips) {
                const pax = trip.bookings.reduce((a, b) => a + b.pax, 0);
                const at = trip.time ?? t('no time');
                if (pax > seatsT)
                    out.push({ level: 'block', code: 'trip_capacity', dayVehicleId: dv.id, bookingIds: trip.bookings.map((b) => b.id), message: t('{v} at {time}: {pax} pax for {seats} seats.', { v, time: at, pax, seats: seatsT }) });
                const places = [...new Set(trip.bookings.map((b) => (b.pickupPlace ?? '').trim().toLowerCase()).filter(Boolean))];
                if (places.length > 1)
                    out.push({ level: 'warning', code: 'trip_two_pickups', dayVehicleId: dv.id, bookingIds: trip.bookings.map((b) => b.id), message: t('{v} at {time}: several pickup places.', { v, time: at }) });
            }
            const timed = trips.filter((x) => x.time);
            for (let i = 1; i < timed.length; i++) {
                const gap = minutes(timed[i].time) - minutes(timed[i - 1].time);
                if (gap < TRIP_GAP_MINUTES)
                    out.push({ level: 'warning', code: 'trips_close', dayVehicleId: dv.id, message: t('{v}: only {n} min between the {a} and {b} trips.', { v, n: gap, a: timed[i - 1].time, b: timed[i].time }) });
            }
            const gapsT = placed.filter((b) => b.missing);
            if (gapsT.length)
                out.push({ level: 'warning', code: 'missing_info', dayVehicleId: dv.id, bookingIds: gapsT.map((b) => b.id), message: t(gapsT.length > 1 ? '{n} bookings in {v} miss information.' : '1 booking in {v} misses information.', { n: gapsT.length, v }) });
            continue;
        }
        // Seats, counted at the busiest part of the day.
        const seats = seatsOf(dv, input.vehicles);
        const load = loadOf(placed, tours);
        const peak = ['am', 'pm', 'eve'].reduce((a, s) => (load[s] > load[a] ? s : a), 'am');
        if (load[peak] > seats) {
            out.push({ level: 'block', code: 'capacity', dayVehicleId: dv.id, message: t('{v} {part}: {pax} pax for {seats} seats.', { v, part: t(SEGMENT_LABELS[peak]).toLowerCase(), pax: load[peak], seats }) });
        }
        // Tours sharing the vehicle at the same time.
        const byTour = new Map();
        for (const b of placed)
            if (b.tourId)
                byTour.set(b.tourId, [...(byTour.get(b.tourId) ?? []), b]);
        const tourIds = [...byTour.keys()];
        for (let i = 0; i < tourIds.length; i++) {
            for (let j = i + 1; j < tourIds.length; j++) {
                const a = tours.get(tourIds[i]);
                const b = tours.get(tourIds[j]);
                if (!a || !b)
                    continue;
                const segs = (t) => new Set(byTour.get(t.id).flatMap((x) => segmentsOf(x, t)));
                const sa = segs(a);
                const common = [...segs(b)].filter((s) => sa.has(s));
                const rule = pairRule(a, b, input.rules);
                const ids = [...byTour.get(a.id), ...byTour.get(b.id)].map((x) => x.id);
                if (rule && !rule.allowed) {
                    out.push({ level: 'block', code: 'forbidden_pair', dayVehicleId: dv.id, bookingIds: ids, message: t('{a} and {b} never share a vehicle.', { a: a.code, b: b.code }) });
                    continue;
                }
                if (!common.length)
                    continue;
                if (!rule) {
                    out.push({ level: 'block', code: 'overlap', dayVehicleId: dv.id, bookingIds: ids, message: t('{a} and {b} run at the same time in {v}.', { a: a.code, b: b.code, v }) });
                    continue;
                }
                for (const s of common) {
                    const da = dirOf(a, s);
                    const db = dirOf(b, s);
                    if (da && db && da !== db) {
                        out.push({ level: 'block', code: 'direction', dayVehicleId: dv.id, bookingIds: ids, message: t('{a} and {b} go opposite ways in the {part}.', { a: a.code, b: b.code, part: t(SEGMENT_LABELS[s]).toLowerCase() }) });
                    }
                }
            }
        }
        // Private and cruise tours: nobody else on board while they run.
        for (const b of placed) {
            const tr = b.tourId ? tours.get(b.tourId) : undefined;
            if (!tr?.dedicatedVehicle)
                continue;
            const segs = segmentsOf(b, tr);
            const others = placed.filter((o) => o.id !== b.id && segmentsOf(o, o.tourId ? tours.get(o.tourId) : undefined).some((s) => segs.includes(s)));
            if (others.length) {
                out.push({ level: 'block', code: 'dedicated', dayVehicleId: dv.id, bookingIds: [b.id, ...others.map((o) => o.id)], message: t('{tour} ({ref}) needs {v} alone.', { tour: tr.code, ref: b.reference, v }) });
            }
        }
        // Languages.
        const langs = [...new Set(placed.map((b) => normLang(b.language)).filter(Boolean))];
        if (langs.length > 2)
            out.push({ level: 'warning', code: 'many_languages', dayVehicleId: dv.id, message: t('{v} mixes {n} languages ({list}).', { v, n: langs.length, list: langs.join(', ') }) });
        // Each guide is checked against the passengers of the part of the day they drive.
        const heard = new Map();
        for (const b of placed) {
            const l = normLang(b.language);
            if (!l)
                continue;
            for (const seg of segmentsOf(b, b.tourId ? tours.get(b.tourId) : undefined)) {
                const g = guideFor(dv, seg);
                if (g)
                    heard.set(g, (heard.get(g) ?? new Set()).add(l));
            }
        }
        for (const [g, ls] of heard) {
            const guide = guides.get(g);
            if (!guide || !guide.languages.length)
                continue;
            const speaks = guide.languages.map(normLang);
            const missingLangs = [...ls].filter((l) => !speaks.includes(l));
            if (missingLangs.length) {
                out.push({ level: 'warning', code: 'guide_language', dayVehicleId: dv.id, message: t('{guide} does not speak {list}.', { guide: guide.name, list: missingLangs.join(', ') }) });
            }
        }
        const gaps = placed.filter((b) => b.missing);
        if (gaps.length) {
            out.push({ level: 'warning', code: 'missing_info', dayVehicleId: dv.id, bookingIds: gaps.map((b) => b.id), message: t(gaps.length > 1 ? '{n} bookings in {v} miss information.' : '1 booking in {v} misses information.', { n: gaps.length, v }) });
        }
    }
    // Bookings that fit in no vehicle, placed or not.
    for (const b of input.bookings) {
        if (live(b) && b.pax > maxSeats) {
            out.push({ level: 'warning', code: 'too_big', bookingIds: [b.id], message: t('{ref}: {pax} pax, more than any vehicle. Split it.', { ref: b.reference, pax: b.pax }) });
        }
    }
    // Tours of the day below their minimum.
    const paxByTour = new Map();
    for (const b of input.bookings)
        if (live(b) && b.tourId)
            paxByTour.set(b.tourId, (paxByTour.get(b.tourId) ?? 0) + b.pax);
    for (const [tourId, pax] of paxByTour) {
        const tr = tours.get(tourId);
        const d = dayTours.get(tourId);
        if (!tr)
            continue;
        if (d?.departure === 'cancelled') {
            const n = input.bookings.filter((b) => live(b) && b.tourId === tourId).length;
            out.push({ level: 'warning', code: 'departure_cancelled', tourId, message: t(n > 1 ? '{tour} cancelled today: {n} bookings to move to another tour or cancel.' : '{tour} cancelled today: 1 booking to move to another tour or cancel.', { tour: tr.code, n }) });
            continue;
        }
        const min = d?.minPax ?? tr.minPax;
        if (tr.kind === 'shared' && d?.departure !== 'guaranteed' && pax < min) {
            out.push({ level: 'warning', code: 'below_minimum', tourId, message: t('{tour}: {pax} pax, minimum {min}. Guarantee or cancel.', { tour: tr.code, pax, min }) });
        }
    }
    return out;
}
const issueKey = (i) => `${i.code}|${i.dayVehicleId ?? ''}|${i.message}`;
/** Blocks a change would add to the plan (the ones already there do not stop other moves). */
export function newBlocks(before, after) {
    const had = new Set(before.filter((i) => i.level === 'block').map(issueKey));
    return after.filter((i) => i.level === 'block' && !had.has(issueKey(i)));
}
/** Why a booking or a vehicle cannot be moved now (locks), or null. */
export function lockReason(input, bookingId, to, t = en) {
    const b = input.bookings.find((x) => x.id === bookingId);
    if (!b)
        return null;
    if (b.locked)
        return t('{ref} is locked. Unlock it first.', { ref: b.reference });
    const from = b.dayVehicleId ? input.dayVehicles.find((d) => d.id === b.dayVehicleId) : undefined;
    if (from?.locked)
        return t('{ref} is locked. Unlock it first.', { ref: `V${from.number}` });
    const dest = to ? input.dayVehicles.find((d) => d.id === to) : undefined;
    if (dest?.locked)
        return t('{ref} is locked. Unlock it first.', { ref: `V${dest.number}` });
    return null;
}
export const NEW_VEHICLE_PREFIX = 'new:';
/**
 * Places the bookings still "to place" without breaking any Block rule and without touching locked
 * bookings or vehicles. Greedy and predictable: private and full-day tours first, biggest groups first;
 * each booking joins a vehicle that already runs its tour, then any vehicle where it fits, tightest
 * first; otherwise a new vehicle is opened.
 */
const lexLess = (a, b) => {
    for (let i = 0; i < a.length; i++)
        if (a[i] !== b[i])
            return a[i] < b[i];
    return false;
};
export function autoPlan(input, maxVehicles = 40, t = en) {
    const tours = new Map(input.tours.map((t) => [t.id, t]));
    const dayTours = new Map((input.dayTours ?? []).map((d) => [d.tourId, d]));
    const work = { ...input, bookings: input.bookings.map((b) => ({ ...b })), dayVehicles: input.dayVehicles.map((d) => ({ ...d })) };
    const result = { moves: [], newVehicles: [], unplaced: [] };
    const rank = (b) => {
        const tr = b.tourId ? tours.get(b.tourId) : undefined;
        return [tr?.dedicatedVehicle ? 0 : 1, segmentsOf(b, tr).length > 1 ? 0 : 1, -b.pax, tr?.code ?? '', b.pickupTime ?? ''];
    };
    const cmp = (a, b) => {
        const ra = rank(a);
        const rb = rank(b);
        for (let i = 0; i < ra.length; i++)
            if (ra[i] !== rb[i])
                return ra[i] < rb[i] ? -1 : 1;
        return 0;
    };
    const todo = work.bookings
        // Transfers are planned in their own vehicles (their grouping comes later), never by the tours Auto-plan.
        .filter((b) => live(b) && !b.dayVehicleId && !b.locked && !b.transfer)
        .sort(cmp);
    for (const b of todo) {
        if (!b.tourId) {
            result.unplaced.push({ bookingId: b.id, reason: t('No tour.') });
            continue;
        }
        if (dayTours.get(b.tourId)?.departure === 'cancelled') {
            result.unplaced.push({ bookingId: b.id, reason: t('Departure cancelled today.') });
            continue;
        }
        const base = checkDay(work, t);
        const fits = (dvId) => {
            b.dayVehicleId = dvId;
            const ok = newBlocks(base, checkDay(work, t)).length === 0;
            b.dayVehicleId = null;
            return ok;
        };
        let best = null;
        for (const dv of work.dayVehicles) {
            if (dv.locked || !fits(dv.id))
                continue;
            const inside = work.bookings.filter((x) => x.dayVehicleId === dv.id && live(x));
            const load = loadOf([...inside, b], tours);
            const free = seatsOf(dv, work.vehicles) - Math.max(load.am, load.pm, load.eve);
            const score = [inside.some((x) => x.tourId === b.tourId) ? 0 : 1, inside.length ? 0 : 1, free, dv.number];
            if (!best || lexLess(score, best.score)) {
                best = { id: dv.id, score };
            }
        }
        if (!best) {
            if (work.dayVehicles.length >= maxVehicles) {
                result.unplaced.push({ bookingId: b.id, reason: t('No vehicle left ({n} at most).', { n: maxVehicles }) });
                continue;
            }
            const number = Math.max(0, ...work.dayVehicles.map((d) => d.number)) + 1;
            const nv = { id: `${NEW_VEHICLE_PREFIX}${result.newVehicles.length + 1}`, number, vehicleId: null, guideId: null, seats: null };
            work.dayVehicles.push(nv);
            b.dayVehicleId = nv.id;
            const why = newBlocks(base, checkDay(work, t));
            b.dayVehicleId = null;
            if (why.length) {
                work.dayVehicles.pop();
                result.unplaced.push({ bookingId: b.id, reason: why[0].message });
                continue;
            }
            result.newVehicles.push({ id: nv.id, number });
            best = { id: nv.id, score: [] };
        }
        b.dayVehicleId = best.id;
        result.moves.push({ bookingId: b.id, to: best.id });
    }
    return result;
}
//# sourceMappingURL=planning.js.map